import {
  FormGroupLabel,
  FormRadioGroup,
  useDialogContext,
} from "@ariakit/react";
import { useLocation } from "wouter";
import { Army } from "../../domain/army";
import { Ruleset } from "../../domain/ruleset";
import { Unit } from "../../domain/unit";
import { UnitData } from "../../lib/db/types";
import { getId } from "../../lib/nanoid";
import { atomic } from "../atomic.css";
import { RadioField } from "../checkbox-field";
import { useDatabase } from "../database-provider";
import { FormDialog } from "../form-dialog";
import { FormField } from "../form-field";
import { useForm } from "../hooks/use-form";

type FormValues = Pick<UnitData, "name" | "description" | "type">;

function UnitDialog(props: {
  initialValues: FormValues;
  onSubmit: (values: FormValues) => Promise<void>;
  submitLabel: string;
  ruleset: Ruleset;
}) {
  const { initialValues } = props;
  const form = useForm({
    onSubmit(values) {
      return props.onSubmit({
        name: values.name.trim(),
        description: values.description.trim(),
        type: values.type,
      });
    },
    defaultValues: {
      name: initialValues.name,
      description: initialValues.description,
      type: initialValues.type,
    },
  });

  const unitProfiles = Array.from(props.ruleset.unitProfiles.values());

  return (
    <FormDialog form={form} submitLabel={props.submitLabel} heading="Unit">
      <FormField
        name={form.names.name}
        label="Name"
        placeholder="E.g. Light Recon Drones"
        autoFocusOnWide
      />

      <FormField
        name={form.names.description}
        label="Description"
        placeholder="Optionally add a backstory, how to play, etc..."
        render={<textarea rows={5} className={atomic({ resize: "none" })} />}
      />

      <FormRadioGroup>
        <FormGroupLabel
          className={atomic({
            borderBlockEnd: "solid",
            paddingBlockEnd: 6,
            fontWeight: "medium",
          })}
        >
          Unit type
        </FormGroupLabel>

        {unitProfiles.map((profile) => {
          return (
            <div
              key={profile.id}
              className={atomic({
                display: "grid",
                gridTemplateColumns: "fraction-auto",
                paddingBlock: 16,
                borderBlockEnd: "dashed",
                fontWeight: "black",
                fontFamily: "arboria",
                fontStyle: "italic",
              })}
            >
              <RadioField
                label={profile.name}
                name={form.names.type}
                value={profile.id}
              />

              <span>{profile.cost}pts</span>
            </div>
          );
        })}
      </FormRadioGroup>
    </FormDialog>
  );
}

export function CreateUnitDialog(props: { army: Army }) {
  const { army } = props;
  const db = useDatabase();
  const dialog = useDialogContext();
  const [_, navigate] = useLocation();

  return (
    <UnitDialog
      submitLabel="Add"
      ruleset={army.ruleset}
      initialValues={{
        name: "",
        description: "",
        type: army.ruleset.defaultUnitProfile.id,
      }}
      onSubmit={async (values) => {
        const id = getId();

        await db.updateArmy({
          ...army.data,
          units: army.data.units.concat({
            id: id,
            created_ts: Date.now(),
            updated_ts: Date.now(),
            keywords: [],
            abilities: [],
            count: 1,
            ...values,
          }),
        });

        dialog?.hide();
        navigate(`/u/${id}`);
      }}
    />
  );
}

export function UpdateUnitDialog(props: { unit: Unit }) {
  const { unit } = props;
  const db = useDatabase();
  const store = useDialogContext();

  return (
    <UnitDialog
      ruleset={props.unit.ruleset}
      initialValues={unit.data}
      submitLabel="Save"
      onSubmit={async (values) => {
        await db.updateArmy({
          ...unit.army.data,
          units: unit.army.data.units.map((u) => {
            if (u.id === unit.data.id) {
              return {
                ...u,
                ...values,
              };
            }

            return u;
          }),
        });

        store?.hide();
      }}
    />
  );
}
